import React from 'react'
import styled from '@emotion/styled'
import { colors } from '@mobi/component-library/Foundations'

type HighlightTextProps = {
  text: string | null | undefined
  children: React.ReactElement
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function highlightNode(
  parent: React.ReactNode | null,
  node: React.ReactNode,
  searchText: string
): React.ReactNode {
  if (!searchText) return node

  if (typeof node === 'string') {
    const escaped = escapeRegExp(searchText)
    const regex = new RegExp(`(${escaped})`, 'gi')
    const parts = node.split(regex)

    return parts.map((part, index) =>
      regex.test(part) ? (
        <HighlightStyled key={index}>{part}</HighlightStyled>
      ) : (
        <React.Fragment key={index}>{part}</React.Fragment>
      )
    )
  }

  if (Array.isArray(node)) {
    return node.map((child, index) => (
      <React.Fragment key={index}>{highlightNode(node, child, searchText)}</React.Fragment>
    ))
  }

  if (React.isValidElement<Record<string, unknown>>(node)) {
    return React.cloneElement(node, {
      ...node.props,
      children: highlightNode(node, node.props.children as React.ReactNode, searchText),
    })
  }

  return node
}

export const HighlightText: React.FC<HighlightTextProps> = ({ text, children }) => {
  if (!text) {
    return children
  }
  return highlightNode(null, children, text)
}

// ======
// Styles
// ======

const HighlightStyled = styled.mark({
  color: colors.studio[500],
  background: '#F2ECF7',
})
